/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import { Injectable } from '@angular/core';
import { HangerSpecification } from '../../../../data-access/src/lib/domain/hanger-specification';
import { SystemInfo } from '../../../../data-access/src/lib/domain/system-info';
import { TabDescription } from '../../../../data-access/src/lib/domain/tab-description';
import { systemSpecification } from '../../../../data-access/src/lib/domain/data/system-spec';
import { toolGroupsData } from '../../../../data-access/src/lib/domain/data/tool-items.data';
import { Observable, from, of } from 'rxjs';
import { BooleanObject, ToolGroup } from '@hcworkspace/sysque/toolbelt/feature';
import { DotNetRequestMessageService } from '@mep/web-browser-bridge';
import { Item } from 'libs/sysque/toolbelt/data-access/src/lib/domain/item';
import { ConfigurationLocationPathPicker } from 'libs/sysque/toolbelt/feature/src/lib/models/configuration-location-return-codes';
import { PanelDetails } from 'libs/sysque/toolbelt/feature/src/lib/models/panel-detail';
import { GlobalConstants } from '../../../../feature/src/lib/constants/global-constants';

@Injectable({
  providedIn: 'root',
})
export class DataInitializerService {
  constructor(private messageHandler: DotNetRequestMessageService) {}

  public InitializeToolbeltAccordionGroupsFromJson(): Observable<ToolGroup[]> {
    const result: ToolGroup[] = toolGroupsData;
    return of(result);
  }

  public InitializeSystemInfo(): Observable<SystemInfo[]> {
    return from<Promise<SystemInfo[]>>(this.messageHandler.sendRequestAsync(GlobalConstants.GetSystems));
  }

  public InitializeHangerSpecificationFromJson(): Observable<HangerSpecification[]> {
    const result: HangerSpecification[] = systemSpecification['hangerSpecifications'] as HangerSpecification[];
    return of(result);
  }

  public InitializeCategoryList(systemName: string): Observable<TabDescription[]> {
    return from<Promise<TabDescription[]>>(this.messageHandler.sendRequestAsync(GlobalConstants.GetCategories, [systemName]));
  }

  public initializeCategoryList(systemName: string): Promise<TabDescription[]> {
    return this.messageHandler.sendRequestAsync(GlobalConstants.GetCategories, [systemName]);
  }

  public updateSystemInfo(): Observable<SystemInfo[]> {
    return from<Promise<SystemInfo[]>>(this.messageHandler.sendRequestAsync(GlobalConstants.GetNewSystems));
  }

  public getFolderPath(): Observable<ConfigurationLocationPathPicker> {
    return from<Promise<ConfigurationLocationPathPicker>>(this.messageHandler.sendRequestAsync(GlobalConstants.OnFolderPickerClick));
  }

  public initializeFittingsList(systemName: string, category: string, isSysQueConnect: boolean): Promise<Item[]> {
    return this.messageHandler.sendRequestAsync(GlobalConstants.GetFittingsBySystemNameAndCategory, [
      systemName,
      category,
      isSysQueConnect
    ]);
  }

  public InitializeRootPath(): Observable<string> {
    return from<Promise<string>>(this.messageHandler.sendRequestAsync(GlobalConstants.GetRootPath));
  }

  public getCircuitList(projectName: string, panelName: string): Promise<string[]> {
    return this.messageHandler.sendRequestAsync(GlobalConstants.GetCircuitList, [projectName, panelName]);
  }

  public InitializeProjectList(): Observable<string[]> {
    return from<Promise<string[]>>(this.messageHandler.sendRequestAsync(GlobalConstants.GetProjectList));
  }

  public initializeWireList(projectName: string, categoryName: string): Promise<string[]> {
    return this.messageHandler.sendRequestAsync(GlobalConstants.GetWireSpecList, [projectName, categoryName]);
  }

  public getProcessStatus(): Observable<BooleanObject> {
    return from<Promise<BooleanObject>>(this.messageHandler.sendRequestAsync(GlobalConstants.GetProcessStatus));
  }

  public checkPanelSystem(systemName: string): Promise<boolean> {
    return this.messageHandler.sendRequestAsync(GlobalConstants.CheckPanelSystem, [systemName]);
  }

  public getDockPanelList(): Promise<PanelDetails[]> {
    return this.messageHandler.sendRequestAsync(GlobalConstants.GetPanelList);
  }

  public updateClearance(systemName: string, isChecked: boolean) {
    return from<Promise<any>>(this.messageHandler.sendRequestAsync(GlobalConstants.UpdateIsClearance, [systemName, isChecked]));
  }

  public onProcessFittingsOnly(categoryName: string, isChecked: boolean): Observable<void> {
    return from<Promise<void>>(this.messageHandler.sendRequestAsync(GlobalConstants.ProcessFittingsOnly, [isChecked, categoryName]));
  }
}
